import type {Editor} from '@tiptap/core';
import {closeHistory} from '@tiptap/pm/history';
import {automationTextContent, automationTextDocument, serializeAutomationText} from './automationVariableText';

export type HtmlSourceFormat = 'bold' | 'italic' | 'underline' | 'bulletList' | 'orderedList' | 'link';
export interface SourceSelection {from: number; to: number}

const sourceBetween = (editor: Editor, from: number, to: number) =>
    serializeAutomationText({content: editor.state.doc.slice(from, to).content.toJSON()});

/** Formatting belongs in text content, never in an HTML tag or one of its attributes. */
export const canFormatHtmlSource = (editor: Editor, range: SourceSelection = editor.state.selection) =>
    !/<[^>]*$/.test(sourceBetween(editor, 0, range.from))
    && !/<[^>]*$/.test(sourceBetween(editor, 0, range.to));

export const isEmailLink = (value: string): boolean => {
    if (/^\[\[[^\[\]\r\n"'<>]+\]\]$/.test(value)) return true;
    if (!/^(https?:\/\/|mailto:|tel:)[^\s<>"']+$/i.test(value)) return false;
    try { return !!new URL(value).protocol; } catch { return false; }
};

/** Each external replacement (including AI) is one undo step, separate from adjacent typing. */
export const replaceAutomationText = (editor: Editor, value: string, placeholders: boolean) => {
    editor.view.dispatch(closeHistory(editor.state.tr));
    editor.commands.setContent(automationTextDocument(value, {placeholders}), {emitUpdate: false});
    editor.view.dispatch(closeHistory(editor.state.tr));
};

/** Insert literal HTML around source text. Parsing it as rich text would discard email markup. */
export const formatHtmlSource = (editor: Editor, format: HtmlSourceFormat, range: SourceSelection = editor.state.selection, href = '') => {
    if (!editor.isEditable || !canFormatHtmlSource(editor, range) || (format === 'link' && !isEmailLink(href))) return false;
    const selected = sourceBetween(editor, range.from, range.to);
    let before: string;
    let body = selected;
    let after: string;
    if (format === 'bulletList' || format === 'orderedList') {
        const tag = format === 'bulletList' ? 'ul' : 'ol';
        before = `<${tag}>\n<li>`;
        body = selected.split(/\r?\n/).join('</li>\n<li>');
        after = `</li>\n</${tag}>`;
    } else if (format === 'link') {
        const attribute = href.startsWith('[[') ? href : href.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        before = `<a href="${attribute}">`;
        after = '</a>';
    } else {
        const tag = {bold: 'strong', italic: 'em', underline: 'u'}[format];
        before = `<${tag}>`;
        after = `</${tag}>`;
    }
    const sizeOf = (text: string) => editor.schema.nodeFromJSON(automationTextDocument(text)).firstChild!.content.size;
    const from = range.from + sizeOf(before);
    const changed = editor.chain().focus()
        .command(({tr}) => { closeHistory(tr); return true; })
        .insertContentAt(range, automationTextContent(before + body + after))
        .setTextSelection({from, to: from + sizeOf(body)})
        .run();
    editor.view.dispatch(closeHistory(editor.state.tr));
    return changed;
};
